<template>
  <v-dialog v-model="show" :retain-focus="false" persistent max-width="800px" :loading="isLoading">
    <v-card>
      <v-card-title>
        <v-row>
          <v-col cols="12" md="12" class="d-flex justify-space-between">
            {{ $t("bookings.checkin_params") }}
            <v-btn icon @click="close">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
          <v-col cols="12" md="12" class="mt-0 pt-0">
            <v-divider></v-divider>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="12">
            <v-row>
              <v-col cols="12" md="4" sm="12">
                <v-checkbox v-model="bookingData.clean_start" :label="$t('bookings.clean_car')" class="mr-1" hide-details></v-checkbox>
              </v-col>
              <v-col cols="12" md="4" sm="12" v-if="showFuel">
                <v-checkbox v-model="bookingData.gas_start_full" :label="$t('bookings.full_tank')"
                  class="mr-1" hide-details></v-checkbox>
              </v-col>
              <v-col cols="12" md="4" sm="12">
                <v-checkbox v-model="bookingData.technical_check_start"
                  :label="$t('bookings.technical_check')" hide-details></v-checkbox>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" md="12">
            <v-divider></v-divider>
          </v-col>
          <v-col cols="12" md="12" v-if="showFuel && !bookingData.gas_start_full">
            <v-text-field v-model="bookingData.gas_start" type="text"
              :label="$t('bookings.lost_gas')" hide-details></v-text-field>
          </v-col>
          <v-col cols="12" md="12" v-if="showMileage">
            <v-text-field :label="$t('bookings.current_mileage')"
              v-model.number="bookingData.start_mileage" type="number" hide-details></v-text-field>
          </v-col>
          <v-col cols="12" md="12">
            <v-text-field v-model="bookingData.other_equipment" type="text"
              :label="$t('bookings.other_equipment')" hide-details></v-text-field>
          </v-col>
          <v-col cols="12" md="12" v-if="!bookingData.technical_check_start">
            <v-text-field v-model="bookingData.technical_check_start_comment" type="text"
              :label="$t('bookings.technical_check_comment')" hide-details></v-text-field>
          </v-col>
          <v-col cols="12" md="12" v-if="company && company.damage_marking && company.damage_marking === true">
            <v-divider class="my-4"></v-divider>
            <car-damage-marker
              ref="carDamageMarker"
              v-model="bookingData.checkin_damages"
              mode="checkin"
              :booking-id="booking.id"
            ></car-damage-marker>
          </v-col>
          <v-col cols="12" v-if="value && damageControlEnabled">
            <damage-control-entry :key="damageControlContext" :booking="booking" side="departure" @blocking="onDamageControlBlocking" />
            <damage-control-strict-alert v-if="damageControlBlocked" side="departure" :lines="damageControlReasonLines" />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="darken-1" text @click="close">{{ $t("custom.close") }}</v-btn>
        <v-btn color="success" @click="checkIn" :loading="isLoading" :disabled="isLoading || isProcessingLocal || damageControlBlocked" data-testid="departure-save">{{ $t('bookings.checkin_car') }}</v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CarDamageMarker from './CarDamageMarker.vue'
import rentalObjectMixin from "@/mixins/rentalObjectMixin";
import DamageControlEntry from "@/components/dashboard/damage_control/entry/DamageControlEntry.vue";
import DamageControlStrictAlert from "@/components/dashboard/damage_control/entry/DamageControlStrictAlert.vue";
import damageControlDialogMixin from "@/components/dashboard/damage_control/entry/dialogMixin";

export default {
  mixins: [rentalObjectMixin, damageControlDialogMixin],
  name: 'DepartureDialog',
  components: {
    CarDamageMarker,
    DamageControlEntry,
    DamageControlStrictAlert
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    booking: {
      type: Object,
      required: true
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      bookingData: {},
      isProcessingLocal: false // Локальная защита от двойного клика
    }
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    company() {
      return this.$store.getters.getCompany.company;
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        // Создаем копию объекта booking при открытии диалога
        this.bookingData = JSON.parse(JSON.stringify(this.booking));
        // Инициализируем поле для повреждений, если его нет
        if (!this.bookingData.checkin_damages) {
          this.$set(this.bookingData, 'checkin_damages', []);
        }
      }
    },
    booking: {
      handler(newVal) {
        // Обновляем bookingData при изменении booking для показа корректного пробега начала бронирования
        if (this.show) {
          this.bookingData = JSON.parse(JSON.stringify(newVal));
        }
      },
      deep: true
    },
    // Сбрасываем локальную блокировку когда родитель сбросил свой флаг
    // (например, когда пользователь нажал "Нет" в диалоге или валидация не прошла)
    isLoading(newVal, oldVal) {
      console.log('DepartureDialog watch: isLoading changed from', oldVal, 'to', newVal, ', isProcessingLocal:', this.isProcessingLocal);
      // Когда родитель завершил обработку (isLoading стал false), сбрасываем локальную блокировку
      if (newVal === false) {
        console.log('DepartureDialog: isLoading became false, resetting isProcessingLocal');
        this.isProcessingLocal = false;
      }
    }
  },
  methods: {
    close() {
      this.isProcessingLocal = false;
      this.show = false;
    },
    async checkIn() {
      if (this.damageControlBlocked) return;
      console.log("checkIn called, isLoading:", this.isLoading, "isProcessingLocal:", this.isProcessingLocal);
      
      // Защита от двойного вызова - проверяем флаг загрузки из родителя
      // isLoading = true означает что родитель обрабатывает запрос
      if (this.isLoading) {
        console.warn("checkIn: parent is loading, skipping");
        return;
      }
      
      // Если родитель не обрабатывает (isLoading = false), сбрасываем локальную блокировку
      // Это нужно потому что watch может не успеть сработать при синхронных изменениях
      if (this.isProcessingLocal) {
        console.log("checkIn: resetting isProcessingLocal because isLoading is false");
        this.isProcessingLocal = false;
      }
      
      // Устанавливаем локальную блокировку
      this.isProcessingLocal = true;
      console.log("checkIn: isProcessingLocal set to true");
      
      try {
        // Создаём callback для загрузки фото повреждений
        // Будет вызван в BookingCard после всех диалогов, перед отправкой на сервер
        const uploadDamageImageCallback = async () => {
          if (this.company && this.company.damage_marking && this.$refs.carDamageMarker) {
            console.log('Starting uploadDamageImageToS3 (checkin), damages count:', this.bookingData.checkin_damages ? this.bookingData.checkin_damages.length : 0);
            try {
              const uploadPromise = this.$refs.carDamageMarker.uploadDamageImageToS3();
              const timeoutPromise = new Promise((resolve) => {
                setTimeout(() => {
                  console.info('Upload timeout reached (30s)');
                  resolve({ success: false, error: 'Upload timeout' });
                }, 30000);
              });
              
              const uploadResult = await Promise.race([uploadPromise, timeoutPromise]);
              console.log('Upload completed (checkin), result:', uploadResult);
              
              if (uploadResult && uploadResult.success) {
                console.log('Checkin damage image uploaded successfully:', uploadResult.image_url);
                return uploadResult.image_url;
              } else if (uploadResult && !uploadResult.success) {
                console.error('Failed to upload checkin damage image:', uploadResult.error);
              }
            } catch (error) {
              console.error('Error uploading checkin damage image:', error);
            }
          } else {
            console.log('Skipping checkin image upload - damage_marking disabled or ref not found');
          }
          return null;
        };
        
        console.log('Emitting check-departure-mileage event with bookingData:', this.bookingData);
        this.$emit('check-departure-mileage', this.bookingData.start_mileage, this.bookingData, uploadDamageImageCallback);
      } catch (error) {
        console.error('Error in checkIn:', error);
        this.isProcessingLocal = false;
        throw error;
      }
    }
  }
}
</script>

